import { useState } from 'react'
import { Plus, Edit, Trash2, CreditCard, Star } from 'lucide-react'
import toast from 'react-hot-toast'
import clientesService from '../../services/clientesService'
import ConfirmDialog from '../ui/ConfirmDialog'

export default function DadosBancariosTab({ cliente, organizadorId, onUpdate }) {
  const [showModal, setShowModal] = useState(false)
  const [editingDado, setEditingDado] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    pix: '',
    observacoes: '',
    is_principal: false
  })

  // Cliente não salvo ainda
  if (!cliente) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm">Salve o cliente primeiro para gerenciar dados bancários.</p>
      </div>
    )
  }

  const handleNew = () => {
    setEditingDado(null)
    setFormData({ banco: '', agencia: '', conta: '', tipo_conta: 'corrente', pix: '', observacoes: '', is_principal: false, titular: '', cpf_cnpj_titular: '', is_terceiro: false })
    setShowModal(true)
  }

  const handleEdit = (dado) => {
    setEditingDado(dado)
    setFormData({
      banco: dado.banco || '',
      agencia: dado.agencia || '',
      conta: dado.conta || '',
      tipo_conta: dado.tipo_conta || 'corrente',
      pix: dado.pix || '',
      observacoes: dado.observacoes || '',
      is_principal: dado.is_principal || false,
      titular: dado.titular || '',
      cpf_cnpj_titular: dado.cpf_cnpj_titular || '',
      is_terceiro: dado.is_terceiro || false,
    })
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingDado) {
        await clientesService.updateDadoBancario(organizadorId, cliente.id, editingDado.id, formData)
      } else {
        await clientesService.createDadoBancario(organizadorId, cliente.id, formData)
      }
      setShowModal(false)
      onUpdate()
    } catch (err) {
      console.error('Erro ao salvar dado bancário:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao salvar dado bancário. Tente novamente.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (dado) => {
    try {
      await clientesService.deleteDadoBancario(organizadorId, cliente.id, dado.id)
      setConfirmDelete(null)
      onUpdate()
    } catch (err) {
      console.error('Erro ao excluir dado bancário:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao excluir dado bancário. Tente novamente.'
      toast.error(errorMessage)
    }
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Dados Bancários</h3>
          <button onClick={handleNew} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nova Conta
          </button>
        </div>

        {(!cliente.dados_bancarios || cliente.dados_bancarios.length === 0) ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CreditCard className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhuma conta cadastrada</h4>
            <p className="text-gray-500 mb-4">Adicione contas bancárias para este cliente</p>
            <button onClick={handleNew} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nova Conta
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cliente.dados_bancarios.map((dado) => (
              <div key={dado.id} className={`border rounded-lg p-4 ${
                dado.is_principal ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'
              }`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{dado.banco}</h4>
                    {dado.is_principal && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(dado)}
                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(dado)}
                      className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Agência:</span> {dado.agencia}</p>
                  <p><span className="font-medium">Conta:</span> {dado.conta}</p>
                  <p><span className="font-medium">Tipo:</span> {dado.tipo_conta === 'corrente' ? 'Corrente' : 'Poupança'}</p>
                  {dado.pix && <p><span className="font-medium">PIX:</span> {dado.pix}</p>}
                  {dado.is_terceiro && dado.titular && (
                    <p className="text-orange-700"><span className="font-medium">Titular:</span> {dado.titular}</p>
                  )}
                  {dado.is_terceiro && dado.cpf_cnpj_titular && (
                    <p className="text-orange-700"><span className="font-medium">CPF/CNPJ Titular:</span> {dado.cpf_cnpj_titular}</p>
                  )}
                  {dado.observacoes && <p><span className="font-medium">Obs:</span> {dado.observacoes}</p>}
                  {dado.is_terceiro && (
                    <p className="text-orange-600 font-medium text-xs mt-1">⚠ Conta de terceiro</p>
                  )}
                  {dado.is_principal && <p className="text-yellow-700 font-medium">✓ Conta Principal</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-lg max-w-md w-full shadow-xl">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingDado ? 'Editar Conta' : 'Nova Conta'}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Banco *</label>
                    <input
                      type="text"
                      required
                      value={formData.banco}
                      onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                      className="input-rematei"
                      placeholder="Ex: Banco do Brasil, Itaú..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Agência *</label>
                      <input
                        type="text"
                        required
                        value={formData.agencia}
                        onChange={(e) => setFormData({ ...formData, agencia: e.target.value })}
                        className="input-rematei"
                        placeholder="0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Conta *</label>
                      <input
                        type="text"
                        required
                        value={formData.conta}
                        onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
                        className="input-rematei"
                        placeholder="00000-0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Conta</label>
                    <select
                      value={formData.tipo_conta}
                      onChange={(e) => setFormData({ ...formData, tipo_conta: e.target.value })}
                      className="input-rematei"
                    >
                      <option value="corrente">Conta Corrente</option>
                      <option value="poupanca">Poupança</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PIX</label>
                    <input
                      type="text"
                      value={formData.pix}
                      onChange={(e) => setFormData({ ...formData, pix: e.target.value })}
                      className="input-rematei"
                      placeholder="CPF, email, telefone ou chave aleatória"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      className="input-rematei"
                      rows={3}
                      placeholder="Observações sobre esta conta bancária..."
                    />
                  </div>

                  {/* Conta de Terceiro */}
                  <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_terceiro"
                        checked={formData.is_terceiro}
                        onChange={(e) => setFormData({ ...formData, is_terceiro: e.target.checked, titular: e.target.checked ? formData.titular : '', cpf_cnpj_titular: e.target.checked ? formData.cpf_cnpj_titular : '' })}
                        className="h-4 w-4 text-rematei-blue focus:ring-rematei-blue border-gray-300 rounded"
                      />
                      <label htmlFor="is_terceiro" className="ml-2 text-sm font-medium text-gray-900">
                        Conta de terceiro
                      </label>
                    </div>

                    {formData.is_terceiro && (
                      <div className="grid grid-cols-1 gap-3 pl-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Titular *</label>
                          <input
                            type="text"
                            required={formData.is_terceiro}
                            value={formData.titular}
                            onChange={(e) => setFormData({ ...formData, titular: e.target.value })}
                            className="input-rematei"
                            placeholder="Nome completo do titular da conta"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ do Titular *</label>
                          <input
                            type="text"
                            required={formData.is_terceiro}
                            value={formData.cpf_cnpj_titular}
                            onChange={(e) => setFormData({ ...formData, cpf_cnpj_titular: e.target.value })}
                            className="input-rematei"
                            placeholder="000.000.000-00"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_principal"
                      checked={formData.is_principal}
                      onChange={(e) => setFormData({ ...formData, is_principal: e.target.checked })}
                      className="h-4 w-4 text-rematei-blue focus:ring-rematei-blue border-gray-300 rounded"
                    />
                    <label htmlFor="is_principal" className="ml-2 text-sm text-gray-900">
                      Conta principal
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? 'Salvando...' : editingDado ? 'Atualizar' : 'Criar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          title="Excluir dado bancário"
          message={`Excluir a conta ${confirmDelete.banco} — ag. ${confirmDelete.agencia} / cc. ${confirmDelete.conta}?`}
          onConfirm={() => handleDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </>
  )
}
